import { message } from 'antd';
import { postOrThrow } from '../utils/ApiUtil';
import { getMock } from '../utils/mockRegistry';
import { mock } from '../common/mockFlag';
import { globalStore } from '../GlobalStore';
import { CommentAE } from '../common/model';
import {
  DemandBrief,
  Project,
  ProjectAddEditParam,
  QueryProjectParam,
} from './model';
import type {
  AddProjectCommentRequestDTO,
  AddProjectRequestDTO,
  EditProjectRequestDTO,
  ProjectDTO,
  ProjectUserDTO,
  QueryProjectRequestDTO,
} from './ProjectType';

const getQueryProjectParam = (searchKey: string, time: Date): QueryProjectParam => {
  const startDate = new Date(time.getFullYear(), time.getMonth(), 1);
  const endDate = new Date(time.getFullYear(), time.getMonth() + 1, 0);
  return new QueryProjectParam(searchKey, startDate, endDate);
};

const resolveUserName = (userId?: string): string => {
  if (!userId) {
    return '';
  }
  return globalStore.userMap.get(userId)?.userName || '';
};

const toDemandBrief = (demandId: string): DemandBrief => {
  const brief = new DemandBrief();
  brief.id = demandId;
  brief.content = '';
  return brief;
};

const toProject = (dto: ProjectDTO): Project => {
  const director = dto.userList?.[0];
  const directorId = director?.userId || dto.fromUserId || '';
  const submitTime = dto.projectDate || new Date();
  const project = new Project();
  project.id = dto.id;
  project.name = dto.projectName || '';
  project.content = dto.projectDesc || '';
  project.businessId = null;
  project.businessName = '';
  project.docLink = dto.projectUrl || '';
  project.submitterId = dto.fromUserId || '';
  project.submitterName = resolveUserName(dto.fromUserId);
  project.directorId = directorId;
  project.directorName = resolveUserName(String(directorId));
  project.submitTime = submitTime;
  project.finishTime = dto.projectDate || submitTime;
  project.status = dto.projectStatus ?? 0;
  project.demands = dto.demandId ? [toDemandBrief(dto.demandId)] : [];
  project.deliveries = [];
  project.comments = [];
  project.logs = [];
  return project;
};

const filterProjectsByRange = (projects: Project[], range: QueryProjectParam): Project[] => {
  const start = range.startDate?.getTime() ?? 0;
  const end = range.endDate ? range.endDate.getTime() + 86400000 - 1 : Number.MAX_SAFE_INTEGER;
  let list = projects.filter((p) => {
    const t = p.submitTime?.getTime() ?? 0;
    return t >= start && t <= end;
  });
  const searchKey = range.searchKey?.trim();
  if (searchKey) {
    const key = searchKey.toLowerCase();
    list = list.filter(
      (p) =>
        p.name?.toLowerCase().includes(key) ||
        String(p.id).toLowerCase().includes(key),
    );
  }
  return list;
};

const toUserList = (project: ProjectAddEditParam): ProjectUserDTO[] => {
  const list: ProjectUserDTO[] = [];
  if (project.directorId != null && project.directorId !== '') {
    list.push({ userId: String(project.directorId), userType: 1 });
  }
  if (
    project.submitterId != null &&
    project.submitterId !== '' &&
    String(project.submitterId) !== String(project.directorId)
  ) {
    list.push({ userId: String(project.submitterId), userType: 0 });
  }
  return list;
};

const toAddRequest = (project: ProjectAddEditParam): AddProjectRequestDTO => ({
  projectName: project.name,
  projectDesc: project.content,
  projectUrl: project.docLink,
  demandId: project.demandIds?.[0] != null ? String(project.demandIds[0]) : undefined,
  projectDate: project.finishDate || new Date(),
  projectStatus: project.status,
  userList: toUserList(project),
});

const toEditRequest = (project: ProjectAddEditParam): EditProjectRequestDTO => ({
  id: String(project.id),
  ...toAddRequest(project),
});

const fetchProjectList = async (query?: QueryProjectRequestDTO): Promise<Project[]> => {
  const list = await postOrThrow<QueryProjectRequestDTO, ProjectDTO[]>(
    '/sparrowProject/queryProjectList',
    query ?? {},
  );
  return (list || []).map(toProject);
};

export const ProjectApi = {
  getQueryProjectParam,

  async getProjectListData(param: Date): Promise<Project[]> {
    if (mock && false) {
      return getMock('projectList') as Project[];
    }
    const dateRange = getQueryProjectParam('', param);
    const projects = await fetchProjectList();
    return filterProjectsByRange(projects, dateRange);
  },

  async addOrEditProject(project: ProjectAddEditParam): Promise<number> {
    if (mock && false) {
      return 12;
    }
    try {
      if (project.id != null && project.id !== '') {
        await postOrThrow<EditProjectRequestDTO, boolean>(
          '/sparrowProject/editProject',
          toEditRequest(project),
        );
        return 0;
      }
      await postOrThrow<AddProjectRequestDTO, string>(
        '/sparrowProject/addProject',
        toAddRequest(project),
      );
      return 0;
    } catch (error) {
      if (project.id) {
        message.error('更新项目失败！');
      } else {
        message.error('创建项目失败！');
      }
      console.error(error);
      return -1;
    }
  },

  async addOrEditComment(comment: CommentAE): Promise<number> {
    if (mock && false) {
      return 12;
    }
    const param: AddProjectCommentRequestDTO = {
      targetId: comment.targetId ? String(comment.targetId) : '',
      targetType: 'project',
      content: comment.content,
      replyCommentId: comment.replyToId ? String(comment.replyToId) : undefined,
    };
    await postOrThrow<AddProjectCommentRequestDTO, unknown>('/comment/addComment', param);
    return 0;
  },

  async deleteProject(id: string | number): Promise<boolean> {
    if (mock && false) {
      return true;
    }
    return postOrThrow<string, boolean>('/sparrowProject/deleteProject', String(id));
  },

  async queryProjects(queryParam: QueryProjectParam): Promise<Project[]> {
    if (mock && false) {
      return getMock('projectList') as Project[];
    }
    const projects = await fetchProjectList();
    return filterProjectsByRange(projects, queryParam);
  },
};

/** @deprecated use ProjectApi */
export const projectApi = {
  getProjectListData: (d: Date) => ProjectApi.getProjectListData(d),
  addOrEditProject: (p: ProjectAddEditParam) => ProjectApi.addOrEditProject(p),
  getQueryProjectParam,
  addOrEditComment: (c: CommentAE) => ProjectApi.addOrEditComment(c),
  deleteProject: (id: string | number) => ProjectApi.deleteProject(id),
  queryProjects: (q: QueryProjectParam) => ProjectApi.queryProjects(q),
};
